// baixarPdf.js — baixa o PDF de um livro que a pessoa tem direito de guardar.
//
// POR QUE ISTO NÃO É UM `<a href>`: o arquivo mora atrás do Bearer, e link
// nenhum carrega cabeçalho de autenticação. Por isso a resposta desce como
// blob e o salvamento é feito à mão — é também o que permite mostrar a recusa
// em português, em vez de abrir uma aba com um JSON de 403.
//
// A MESMA LÓGICA JÁ EXISTIA em hooks/useEstante.js, onde nasceu para "Meus
// E-books". Ela saiu de lá para cá quando a página do livro passou a precisar
// dela também: com a cópia digital grátis, quem NÃO comprou o título também
// baixa o arquivo, e a estante não é o caminho dessa pessoa — ela nunca vai
// passar por lá, porque o livro não está na conta dela.
//
// (A estante segue com a cópia dela por ora. Quando as duas divergirem, esta é
// a que vale: é a que o servidor espelha.)
const API_URL = process.env.REACT_APP_API;

const pegarToken = () =>
  localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || ""));

/** Nome de arquivo legível a partir do título, sem acento nem pontuação. */
const nomeDoArquivo = (titulo) => {
  const limpo = String(titulo || "livro")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return (limpo || "livro") + ".pdf";
};

/**
 * A recusa do servidor, dita em português.
 *
 * O 403 tem DOIS significados muito diferentes aqui, e a mensagem precisa
 * separá-los: "você não tem este livro" manda a pessoa comprar, "o download
 * está desligado" manda ela ler no site. O servidor distingue os dois pelo
 * campo `motivo`; sem ele, sobra o texto genérico.
 */
const mensagemDeErro = (status, corpo) => {
  if (corpo?.motivo === "download_desativado") {
    return corpo.message || "O download está desativado. Você pode ler o livro aqui no site.";
  }
  if (status === 401) return "Sua sessão expirou. Entre novamente para baixar.";
  if (status === 403) return "Este livro não está liberado para download na sua conta.";
  if (status === 404) return "O arquivo deste livro não foi encontrado.";
  return "Não foi possível baixar agora. Tente de novo em instantes.";
};

/**
 * Baixa o PDF do livro. Lança Error com a mensagem pronta para a tela mostrar.
 *
 * @param {{id: number|string, titulo?: string}} livro
 */
export const baixarPdfDoLivro = async (livro) => {
  if (!livro?.id) return;

  const token = pegarToken();
  if (!token) throw new Error("Entre na sua conta para baixar o arquivo.");

  const resp = await fetch(
    `${API_URL}api/usuario-editora/get-pdf?book_id=${encodeURIComponent(livro.id)}`,
    { headers: { Authorization: "Bearer " + token } }
  );

  if (!resp.ok) {
    let corpo = null;
    try {
      corpo = await resp.json();
    } catch {
      /* recusa sem JSON: a mensagem por status já resolve */
    }
    throw new Error(mensagemDeErro(resp.status, corpo));
  }

  const blob = await resp.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeDoArquivo(livro.titulo);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Sem o revoke o blob fica na memória da aba até ela fechar — e são dezenas
  // de MB por livro.
  window.URL.revokeObjectURL(url);
};

export default baixarPdfDoLivro;
